import { useEffect, useState, useContext } from 'react';
import api from '../api/api';
import {
  Typography, CircularProgress, Box,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Select, MenuItem, InputLabel, FormControl, FormHelperText,
  InputAdornment, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { AuthContext } from '../context/AuthContext';
import ServiceGrid from '../components/ServiceGrid';
import CarruselDestacados from '../components/CarruselDestacados';
import { SERVICIOS_DESTACADOS, DESTACADOS_SERVICIOS } from '../config/serviciosDestacados';
import { serviciosVisibles, serviciosDestacados, filtrarServicios } from '../utils/servicios';

const font = "'Montserrat', sans-serif";

// Rediseño de /services (SERVICIOS-GRILLA-UNIFICADA, opción C con los chips
// de la A, 28-10-2026). Los chips son las categorías del CHECK de la base, de
// GET /services/categorias: si la maqueta dice otra cosa, gana la base.
const Services = () => {
  const { user } = useContext(AuthContext);
  const esAdmin = user?.role === 'admin';
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [categoriasError, setCategoriasError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({ id: null, title: '', description: '', category: '', availableServices: '', icon: '', price: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => { fetchServices(); fetchCategorias(); }, []);

  const fetchServices = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get('/services');
      setServices(res.data);
    } catch (error) {
      console.error('Error fetching services', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  // Las 7 categorías: dan los chips y el desplegable del admin. Si fallan, no
  // hay chips (el buscador sigue andando) y el admin no puede elegir categoría.
  const fetchCategorias = async () => {
    try {
      const { data } = await api.get('/services/categorias');
      setCategorias(data);
      setCategoriasError(false);
    } catch (error) {
      console.error('Error fetching categorias', error);
      setCategoriasError(true);
    }
  };

  const visibles = serviciosVisibles(services, esAdmin)
    .sort((a, b) => a.title.localeCompare(b.title, 'es'));
  const filtrando = !!categoryFilter || search.trim() !== '';
  const filteredServices = filtrarServicios(visibles, search, categoryFilter);
  const destacados = serviciosDestacados(visibles, SERVICIOS_DESTACADOS, DESTACADOS_SERVICIOS);

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleOpenDialog = (service = null) => {
    setFormData(service
      ? { id: service.id, title: service.title, description: service.description || '', category: service.category || '', availableServices: service.availableServices || '', icon: service.icon || '', price: service.price || '' }
      : { id: null, title: '', description: '', category: '', availableServices: '', icon: '', price: '' }
    );
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (formData.title.length < 3 || formData.description.length < 10 || !formData.category) {
      setSnackbar({ open: true, message: 'Por favor completá los campos correctamente.', severity: 'warning' });
      return;
    }
    const token = localStorage.getItem('token');
    try {
      if (formData.id) {
        const res = await api.put(`/services/${formData.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        setServices(prev => prev.map(s => s.id === formData.id ? res.data : s));
        setSnackbar({ open: true, message: 'Servicio actualizado.', severity: 'success' });
      } else {
        const res = await api.post('/services', formData, { headers: { Authorization: `Bearer ${token}` } });
        setServices(prev => [...prev, res.data]);
        setSnackbar({ open: true, message: 'Servicio creado.', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al guardar el servicio.', severity: 'error' });
    } finally {
      setDialogOpen(false);
    }
  };

  // Borrar un servicio arrastra en cascada su formulario, calificaciones y
  // postulaciones (FK ON DELETE CASCADE). El diálogo lo dice con los números
  // reales antes de confirmar; sin los números no se deja borrar (26-10-2026).
  const [impacto, setImpacto] = useState({ cargando: false, datos: null, error: false });

  const abrirBorrado = async (id) => {
    setConfirmDialog({ open: true, id });
    setImpacto({ cargando: true, datos: null, error: false });
    try {
      const { data } = await api.get(`/services/${id}/borrado`);
      setImpacto({ cargando: false, datos: data, error: false });
    } catch {
      setImpacto({ cargando: false, datos: null, error: true });
    }
  };

  const n = (k, uno, varios) => `${k} ${k === 1 ? uno : varios}`;

  const handleDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      await api.delete(`/services/${confirmDialog.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setServices(prev => prev.filter(s => s.id !== confirmDialog.id));
      setSnackbar({ open: true, message: 'Servicio eliminado.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al eliminar.', severity: 'error' });
    } finally {
      setConfirmDialog({ open: false, id: null });
    }
  };

  const tituloSeccion = { fontFamily: font, fontSize: 16, fontWeight: 800, color: '#163244', mb: 1.5 };
  const chipSx = (activo) => ({
    fontFamily: font, fontWeight: 700, fontSize: 12.5,
    bgcolor: activo ? '#163244' : '#fff',
    color: activo ? '#fff' : '#163244',
    border: '1px solid', borderColor: activo ? '#163244' : 'rgba(0,0,0,0.12)',
    '&:hover': { bgcolor: activo ? '#163244' : '#fff', borderColor: '#E7A432' },
  });

  return (
    <Box sx={{ fontFamily: font }}>
      {/* Banda */}
      <Box sx={{ bgcolor: '#163244', px: { xs: 2, md: 6 }, pt: 4, pb: 4.5 }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', position: 'relative' }}>
          {esAdmin && (
            <Button variant="outlined" onClick={() => handleOpenDialog()}
              sx={{
                position: { md: 'absolute' }, top: 0, right: 0, mb: { xs: 2, md: 0 },
                bgcolor: '#fff', color: '#163244', borderColor: '#163244', fontWeight: 700, fontFamily: font,
                '&:hover': { bgcolor: '#fff', borderColor: '#E7A432' },
              }}>
              + Agregar servicio
            </Button>
          )}
          <Typography sx={{ fontFamily: font, fontSize: 11, fontWeight: 700, color: '#E7A432', letterSpacing: 2, mb: 1 }}>
            SERVICIOS
          </Typography>
          <Typography component="h1" sx={{ fontFamily: font, fontSize: { xs: 22, md: 28 }, fontWeight: 800, color: '#fff', mb: 0.8 }}>
            ¿Qué necesitás resolver?
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: 13, color: 'rgba(255,255,255,0.7)', mb: 2.5 }}>
            Elegí un servicio, contanos qué necesitás y recibí presupuestos de prestadores.
          </Typography>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar un servicio"
            size="small"
            inputProps={{ 'aria-label': 'Buscar un servicio' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#7a93a4' }} /></InputAdornment>,
            }}
            sx={{
              width: '100%', maxWidth: 460,
              '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '12px', fontFamily: font },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 6 }, pt: 3, pb: 6 }}>
        {/* Chips de categoría */}
        {!categoriasError && categorias.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3.5 }}>
            <Chip label="Todos" clickable onClick={() => setCategoryFilter('')} sx={chipSx(!categoryFilter)} />
            {categorias.map(cat => (
              <Chip key={cat} label={cat} clickable onClick={() => setCategoryFilter(cat)} sx={chipSx(categoryFilter === cat)} />
            ))}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: '#E7A432' }} /></Box>
        ) : loadError ? (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography sx={{ fontFamily: font, color: '#163244', fontWeight: 700, mb: 1.5 }}>
              No pudimos cargar los servicios.
            </Typography>
            <Button variant="outlined" onClick={fetchServices} sx={{ borderColor: '#163244', color: '#163244', fontFamily: font }}>
              Reintentar
            </Button>
          </Box>
        ) : (
          <>
            {/* Con un chip elegido o texto en el buscador, los destacados se ocultan. */}
            {!filtrando && destacados.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <CarruselDestacados services={destacados} titulo="Los más pedidos" tituloSx={tituloSeccion} esAdmin={esAdmin}
                  onEditar={handleOpenDialog} onEliminar={(s) => abrirBorrado(s.id)} />
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
              <Typography component="h2" sx={{ ...tituloSeccion, mb: 0 }}>Todos los servicios</Typography>
              <Typography sx={{ fontFamily: font, fontSize: 13, color: '#7a93a4', fontWeight: 600 }}>
                {filtrando ? `${filteredServices.length} de ${visibles.length}` : visibles.length}
              </Typography>
            </Box>
            {filteredServices.length > 0 ? (
              <ServiceGrid services={filteredServices} variante="compacto" esAdmin={esAdmin}
                onEditar={handleOpenDialog} onEliminar={(s) => abrirBorrado(s.id)} />
            ) : (
              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Typography sx={{ fontFamily: font, color: '#7a93a4' }}>No se encontraron servicios.</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Dialog admin: crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#163244', color: '#fff' }}>
          {formData.id ? 'Editar servicio' : 'Agregar servicio'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>

          {/* Fila 1: Título + Categoría */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <TextField
              label="Título" name="title" size="small"
              sx={{ flex: 2 }} margin="dense"
              value={formData.title} onChange={handleInputChange}
              required
            />
            {/* Desplegable con las categorías del CHECK: el backend rechaza
                cualquier otra, y con texto libre el admin veía "Error al
                guardar" sin saber por qué (03-11-2026). */}
            <FormControl size="small" sx={{ flex: 1 }} margin="dense" required error={categoriasError}>
              <InputLabel>Categoría</InputLabel>
              <Select name="category" label="Categoría" value={formData.category}
                onChange={handleInputChange} disabled={categoriasError}>
                {categorias.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </Select>
              {categoriasError && <FormHelperText>No se pudieron cargar las categorías.</FormHelperText>}
            </FormControl>
            <TextField
              label="Precio estimado ($)" name="price" size="small"
              type="number" sx={{ flex: 1 }} margin="dense"
              value={formData.price} onChange={handleInputChange}
            />
          </Box>

          {/* Descripción */}
          <TextField
            label="Descripción" name="description" fullWidth
            size="small" margin="dense" multiline rows={3}
            value={formData.description} onChange={handleInputChange}
            required
          />

          {/* Servicios disponibles */}
          <TextField
            label="Servicios disponibles (uno por línea)" name="availableServices"
            fullWidth size="small" margin="dense" multiline rows={4}
            value={formData.availableServices} onChange={handleInputChange}
            helperText="Listá los subservicios que ofrece este rubro, uno por línea"
          />

          {/* Ícono */}
          <TextField
            label="Nombre del ícono" name="icon" fullWidth
            size="small" margin="dense"
            value={formData.icon} onChange={handleInputChange}
            helperText="Ej: plomero.png — debe estar en /assets/images/icons/"
          />

          {/* Preview del ícono si existe */}
          {formData.icon && (() => {
            try {
              const src = require(`../assets/images/icons/${formData.icon}`);
              return (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="img" src={src} alt="preview"
                    sx={{ width: 40, height: 40, objectFit: 'contain' }} />
                  <Typography variant="caption" color="text.secondary">
                    Vista previa del ícono
                  </Typography>
                </Box>
              );
            } catch { return null; }
          })()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined"
            sx={{ borderColor: '#163244', color: '#163244' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}
            sx={{ backgroundColor: '#E7A432', color: '#163244' }}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminar */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, id: null })}>
        <DialogTitle>¿Eliminar {impacto.datos?.title || 'este servicio'}?</DialogTitle>
        <DialogContent>
          {impacto.cargando && <CircularProgress size={20} />}
          {impacto.error && (
            <Typography color="error" variant="body2">
              No pudimos calcular qué se borra junto con el servicio. Sin eso no se puede eliminar; probá de nuevo más tarde.
            </Typography>
          )}
          {impacto.datos && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Esto borra también, sin vuelta atrás: el formulario de este rubro ({n(impacto.datos.campos, 'campo', 'campos')}),{' '}
                {n(impacto.datos.calificaciones, 'calificación', 'calificaciones')} y {n(impacto.datos.postulaciones, 'postulación', 'postulaciones')}.
              </Typography>
              {impacto.datos.solicitudes > 0 && (
                <Typography variant="body2" color="error">
                  Este rubro tiene {n(impacto.datos.solicitudes, 'solicitud', 'solicitudes')}: un rubro en uso no se puede eliminar.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, id: null })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={!impacto.datos || impacto.datos.solicitudes > 0}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Services;
